import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Faq } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, Button, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess } from '../../utils/helpers';
import { getIconButtonStyle } from '../../utils/styles';
import { IntegrationsMetadata } from '../../utils/typings';

interface FaqTabProps {
  integrationsMetadata?: IntegrationsMetadata;
  userRole?: keyof typeof UserRole;
  faqs?: Faq[];
}

const FaqTab: React.FC<FaqTabProps> = ({ integrationsMetadata, userRole, faqs = [] }) => {
  const filteredFaqs = useMemo(() => {
    if (!faqs) return [];
    return faqs.filter((faq) => faq.question && faq.answer);
  }, [faqs]);

  const hasFaqsAccess = useMemo(
    () => (integrationsMetadata?.faqs ? checkRbacAccess(integrationsMetadata.faqs, userRole).hasViewAccess : true),
    [integrationsMetadata, userRole]
  );

  if (filteredFaqs.length === 0 || !hasFaqsAccess) {
    return (
      <Text textAlign='left' css={emptyStateStyle}>
        No FAQ's available.
      </Text>
    );
  }

  const renderFaqItem = (faq: Faq, index: number) => (
    <Accordion.Item key={index} value={`faq-${faq.question}-${index}`}>
      <Accordion.Header>
        <Text weight='bold' textAlign='left'>
          {faq.question}
        </Text>
      </Accordion.Header>

      <Accordion.Body>
        <div css={answerContainerStyle}>
          <Text textAlign='left'>{faq.answer}</Text>
          <div css={actionsStyle}>
            <Button aria-label='Edit' css={getIconButtonStyle(theme.colors.black)}>
              <Icon name='edit' size={20} />
            </Button>
            <Button aria-label='Remove' css={getIconButtonStyle(theme.colors.status.critical)}>
              <Icon name='remove2' size={20} />
            </Button>
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );

  const leftFaqs = filteredFaqs.filter((_, index) => index % 2 === 0);
  const rightFaqs = filteredFaqs.filter((_, index) => index % 2 !== 0);

  return (
    <div css={containerStyle}>
      <div css={headerStyle}>
        <Button aria-label='Add New FAQ' iconName='add' variant='tertiary' size='large'>
          Add New FAQ
        </Button>
      </div>

      <Accordion variant='main' isMulti css={accordionStyle} showBoxShadow>
        <div css={columnStyle}>{leftFaqs.map((faq, i) => renderFaqItem(faq, i * 2))}</div>
        <div css={columnStyle}>{rightFaqs.map((faq, i) => renderFaqItem(faq, i * 2 + 1))}</div>
      </Accordion>
    </div>
  );
};

export default FaqTab;

const containerStyle = css`
  display: flex;
  flex-direction: column;
  margin-top: ${theme.spacing(3)};
`;

const headerStyle = css`
  display: flex;
  justify-content: flex-start;
  margin-bottom: ${theme.spacing(2)};
`;

const accordionStyle = css`
  display: flex;
  gap: ${theme.spacing(2)};
  align-items: flex-start;
`;

const columnStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${theme.spacing(0.5)};
  min-width: 0;
`;

const answerContainerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing(2)};
`;

const actionsStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
  align-items: center;
  flex-shrink: 0;
`;

const emptyStateStyle = css`
  margin-top: ${theme.spacing(2)};
`;
